/**
 * Rehabilitation Fund Module Page (Redesigned - Korat Health Style)
 *
 * Service types and disbursement tracking
 */

import { useState } from 'react'
import {
  Activity,
  DollarSign,
  Users,
  TrendingUp,
  BarChart,
  Hand,
  MessageSquare,
  Brain,
  Settings,
  Briefcase,
  Home,
  MoreHorizontal,
  type LucideIcon,
} from 'lucide-react'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  DataTable,
  ProgressBar,
  YearFilter,
  DistrictFilter,
  type Column,
} from '../shared/components'
import { AreaChartCard } from '../shared/components/charts'
import { useRehabOverview, useRehabByDistrict, useRehabTrends, useDistricts } from '../services/api'
import type { RehabCategory } from '../types/modules'
import { formatNumber } from '../shared/utils/formatNumber'

// Icon mapper for rehab categories
const getRehabIcon = (iconName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    activity: Activity,
    hand: Hand,
    'message-square': MessageSquare,
    brain: Brain,
    settings: Settings,
    briefcase: Briefcase,
    home: Home,
    'more-horizontal': MoreHorizontal,
  }
  return iconMap[iconName] || Activity
}

// Icon colors based on rehab service type
const getRehabColor = (index: number): string => {
  const colors = [
    'bg-emerald-100 text-emerald-600',   // Physical therapy
    'bg-blue-100 text-blue-600',         // Occupational therapy
    'bg-purple-100 text-purple-600',     // Speech therapy
    'bg-pink-100 text-pink-600',         // Mental/behavioral
    'bg-korat-orange-100 text-korat-orange-600', // Equipment
    'bg-cyan-100 text-cyan-600',         // Vocational training
    'bg-slate-100 text-slate-600',       // Home care
    'bg-gray-100 text-gray-600',         // Other
  ]
  return colors[index % colors.length]
}

export function RehabPage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  const overviewQuery = useRehabOverview(selectedYear)
  const districtQuery = useRehabByDistrict(selectedDistrict || 0, selectedYear)

  const data = selectedDistrict ? districtQuery.data : overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const error = selectedDistrict ? districtQuery.error : overviewQuery.error

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Fetch 5-year trends
  const { data: trendsData, isLoading: trendsLoading } = useRehabTrends({
    districtId: selectedDistrict || undefined,
  })

  const avgCostPerPerson = data ? (parseFloat(data.totals.grand_disbursed) / parseFloat(data.totals.grand_beneficiaries)) : 0

  const columns: Column<RehabCategory>[] = [
    {
      header: 'ประเภทบริการ',
      accessor: 'name_th',
      width: 'w-1/4',
      render: (value, row, index) => {
        const Icon = getRehabIcon(row.icon_name)
        const colorClass = getRehabColor(index || 0)
        return (
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${colorClass}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="font-semibold text-korat-navy-700 tabular-nums">{value as string}</div>
              <div className="text-xs text-slate-500">{row.service_type}</div>
            </div>
          </div>
        )
      },
    },
    {
      header: 'รายได้',
      accessor: 'total_income',
      align: 'right',
      render: (value) => <span className="text-emerald-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'จ่ายออก',
      accessor: 'total_disbursed',
      align: 'right',
      render: (value) => <span className="text-korat-orange-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'ผู้รับบริการ',
      accessor: 'total_beneficiaries',
      align: 'right',
      render: (value) => <span className="tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'ค่าใช้จ่ายต่อคน',
      accessor: 'avg_per_person',
      align: 'right',
      render: (value) => <span className="text-korat-navy-700 tabular-nums">{formatNumber(value as string)} ฿</span>,
    },
    {
      header: 'อัตราการใช้',
      accessor: 'avg_utilization',
      align: 'right',
      width: 'w-32',
      render: (value) => <ProgressBar value={parseFloat(value as string)} variant="info" />,
    },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="rehab" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Activity}
          title="กองทุนฟื้นฟูสมรรถภาพ"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
            </>
          }
        />

        <div className="p-6 space-y-6">
          {/* KPI Cards */}
          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Total Income */}
              <KpiCardRich
                icon={DollarSign}
                title="รายได้ทั้งหมด"
                description="เงินที่ได้รับจากทุกแหล่งสำหรับกองทุนฟื้นฟู"
                value={data?.totals.grand_income || 0}
                unit="บาท"
                helperText="จากเงินสมทบและงบประมาณแผ่นดิน"
                breakdown={[
                  `<b>เงินสมทบ UC</b> ${formatNumber((parseFloat(data?.totals.grand_income || '0') * 0.60).toFixed(0))}`,
                  `<b>งบแผ่นดิน</b> ${formatNumber((parseFloat(data?.totals.grand_income || '0') * 0.30).toFixed(0))}`,
                  `<b>อื่นๆ</b> ${formatNumber((parseFloat(data?.totals.grand_income || '0') * 0.10).toFixed(0))}`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Total Disbursed */}
              <KpiCardRich
                icon={TrendingUp}
                title="จ่ายออกทั้งหมด"
                description="เงินที่จ่ายให้ผู้รับบริการฟื้นฟูสมรรถภาพ"
                value={data?.totals.grand_disbursed || 0}
                unit="บาท"
                helperText={`คงเหลือ ${formatNumber((parseFloat(data?.totals.grand_income || '0') - parseFloat(data?.totals.grand_disbursed || '0')))} บาท`}
                breakdown={[
                  `<b>รายได้</b> ${formatNumber(data?.totals.grand_income || 0)}`,
                  `<b>จ่ายออก</b> ${formatNumber(data?.totals.grand_disbursed || 0)}`,
                  `<b>คงเหลือ</b> ${formatNumber((parseFloat(data?.totals.grand_income || '0') - parseFloat(data?.totals.grand_disbursed || '0')))}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Beneficiaries */}
              <KpiCardRich
                icon={Users}
                title="ผู้รับบริการ"
                description="จำนวนผู้ที่ได้รับบริการฟื้นฟูสมรรถภาพ"
                value={data?.totals.grand_beneficiaries || 0}
                unit="คน"
                helperText={`เฉลี่ย ${avgCostPerPerson.toLocaleString('th-TH', { maximumFractionDigits: 0 })} บาทต่อคน`}
                breakdown={[
                  `<b>กายภาพบำบัด</b> ${formatNumber(data?.by_category.find(c => c.service_type === 'Physical Therapy')?.total_beneficiaries || 0)}`,
                  `<b>อาชีวบำบัด</b> ${formatNumber(data?.by_category.find(c => c.service_type === 'Occupational Therapy')?.total_beneficiaries || 0)}`,
                  `<b>อื่นๆ</b> ${formatNumber((parseFloat(data?.totals.grand_beneficiaries || '0') - parseFloat(data?.by_category.find(c => c.service_type === 'Physical Therapy')?.total_beneficiaries || '0') - parseFloat(data?.by_category.find(c => c.service_type === 'Occupational Therapy')?.total_beneficiaries || '0')))}`,
                ]}
                isLoading={isLoading}
                variant="blue"
              />

              {/* Utilization Rate */}
              <KpiCardRich
                icon={BarChart}
                title="อัตราการใช้เฉลี่ย"
                description="เปอร์เซ็นต์การใช้จ่ายเทียบกับรายได้"
                value={parseFloat(data?.totals.avg_utilization || '0')}
                unit="%"
                helperText="เป้าหมาย 85% เพื่อสำรองเหลื่อม"
                breakdown={[
                  `<b>สูงสุด</b> ${data?.by_category.reduce((max, c) => Math.max(max, parseFloat(c.avg_utilization.toString())), 0).toFixed(1)}%`,
                  `<b>ต่ำสุด</b> ${data?.by_category.reduce((min, c) => Math.min(min, parseFloat(c.avg_utilization.toString())), 100).toFixed(1)}%`,
                  `<b>เฉลี่ย</b> ${parseFloat(data?.totals.avg_utilization || '0').toFixed(1)}%`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />
            </div>
          </section>

          {/* Categories Table */}
          <section>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                จำแนกตามประเภทบริการ
              </h2>
              <DataTable
                columns={columns}
                data={data?.by_category || []}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลบริการ"
              />
            </div>
          </section>

          {/* Cost Analysis */}
          <section>
            <div className="bg-white rounded-xl border-2 border-korat-orange-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                การวิเคราะห์ต้นทุน
              </h2>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-20 bg-slate-200 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data?.by_category.slice(0, 4).map((category, index) => {
                    const income = parseFloat(category.total_income.toString())
                    const disbursed = parseFloat(category.total_disbursed.toString())
                    const balance = income - disbursed
                    const utilization = parseFloat(category.avg_utilization.toString())
                    const Icon = getRehabIcon(category.icon_name)
                    const colorClass = getRehabColor(index)

                    return (
                      <div
                        key={category.id}
                        className="p-4 rounded-lg border border-slate-200 bg-slate-50"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`rounded-lg p-2 ${colorClass}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="font-semibold text-korat-navy-700 tabular-nums">
                            {category.name_th}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">รายได้:</span>
                            <span className="font-semibold text-emerald-600 tabular-nums">
                              {formatNumber(income)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">จ่ายออก:</span>
                            <span className="font-semibold text-korat-orange-600 tabular-nums">
                              {formatNumber(disbursed)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">คงเหลือ:</span>
                            <span className="font-semibold text-korat-navy-700 tabular-nums">
                              {formatNumber(balance)}
                            </span>
                          </div>
                          <div className="pt-2 border-t border-slate-200">
                            <ProgressBar
                              label="อัตราการใช้"
                              value={utilization}
                              variant={utilization >= 80 ? 'success' : 'warning'}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Rehab Fund Trends Chart */}
          <section>
              <AreaChartCard
                title="แนวโน้มกองทุนฟื้นฟู 5 ปี (พ.ศ. 2563-2567)"
                description="รายได้และการจ่ายออกของกองทุนฟื้นฟูสมรรถภาพในช่วง 5 ปีที่ผ่านมา"
                data={
                  trendsData?.trends && Array.isArray(trendsData.trends)
                    ? (() => {
                        const yearMap = new Map<number, any>()

                        trendsData.trends.forEach((item: any) => {
                          const year = item.year
                          if (!yearMap.has(year)) {
                            yearMap.set(year, { year: `พ.ศ. ${year}`, income: 0, disbursed: 0 })
                          }
                          const yearData = yearMap.get(year)
                          yearData.income += parseFloat(item.total_income || 0)
                          yearData.disbursed += parseFloat(item.total_disbursed || 0)
                        })

                        return Array.from(yearMap.values())
                          .sort((a, b) => parseInt(a.year.replace('พ.ศ. ', '')) - parseInt(b.year.replace('พ.ศ. ', '')))
                          .slice(-5)
                      })()
                    : []
                }
                areas={[
                  { dataKey: 'income', name: 'รายได้', color: 'green' },
                  { dataKey: 'disbursed', name: 'จ่ายออก', color: 'orange' },
                ]}
                xAxisKey="year"
                height={400}
                isLoading={trendsLoading}
              />
          </section>
        </div>
      </main>
    </div>
  )
}
